import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import { fetchRequestResponseData } from "utils/commonFunctions";

interface NsoFlowData {
  stepID: number;
  stepName: string;
  data: any[];
  isSuccess: boolean;
}
export interface NsoFlowState {
  data: NsoFlowData[];
  activeStep: number;
  loading: boolean;
  error: any;
}

const nsoFlowInitialState: NsoFlowState | null = {
  data: [
    {
      stepID: 1,
      stepName: "NSO Request",
      data: [],
      isSuccess: true,
    },
    {
      stepID: 2,
      stepName: "Vehicle Details",
      data: [],
      isSuccess: true,
    },
    {
      stepID: 3,
      stepName: "Check Vehicle Eligibility",
      data: [],
      isSuccess: true,
    },
    {
      stepID: 4,
      stepName: "Get Spec From TOMS",
      data: [],
      isSuccess: true,
    },
    {
      stepID: 6,
      stepName: "Update Non Buildable Spec",
      data: [],
      isSuccess: true,
    },
    {
      stepID: 7,
      stepName: "Get Inline Spec From Pcps",
      data: [],
      isSuccess: true,
    },
    {
      stepID: 9,
      stepName: "Post Order Change To SimEngine",
      data: [],
      isSuccess: true,
    },
  ],
  activeStep: 0,
  loading: false,
  error: null,
};

const storedState: any = localStorage.getItem("nsoFlowState");
const initialState =
  storedState && JSON.parse(storedState).length > 0
    ? JSON.parse(storedState)
    : {
        nsoFlow: nsoFlowInitialState,
      };

export const nsoFlowFetchNsoRequestData = createAsyncThunk(
  "nsoFlow/nsoFlowFetchNsoRequestData",
  async ({ id, stepId }: { id: string; stepId: number }) => {
    const response = await fetchRequestResponseData(id, stepId.toString());
    return { id: id, data: response, stepId: stepId };
  },
);

export const nsoFlowSlice = createSlice({
  name: "nsoFlow",
  initialState: initialState,
  reducers: {
    nsoFlowFetchData: (state) => {
      state.nsoFlow.loading = true;
      state.nsoFlow.error = null;
    },
    nsoFlowFetchDataSuccess: (
      state,
      action: PayloadAction<{
        stepID: number;
        data: any[];
      }>,
    ) => {
      state.nsoFlow.data.forEach((step: any) => {
        if (step.stepID === action.payload.stepID) {
          step.data = action.payload.data;
        }
      });
      state.nsoFlow.loading = false;
      state.nsoFlow.error = null;
    },
    nsoFlowFetchDataFailure: (state, action: PayloadAction<any>) => {
      state.nsoFlow.loading = false;
      state.nsoFlow.error = action.payload;
    },
    nsoFlowSetActiveStep: (state, action: PayloadAction<number>) => {
      state.nsoFlow.activeStep = action.payload;
    },
    nsoFlowStateReset: (state) => {
      state.nsoFlow = nsoFlowInitialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(nsoFlowFetchNsoRequestData.pending, (state) => {
      state.nsoFlow.loading = true;
      state.nsoFlow.error = null;
    });
    builder.addCase(nsoFlowFetchNsoRequestData.fulfilled, (state, action) => {
      state.nsoFlow.data.forEach((step: any) => {
        if (step.stepID === action.payload.stepId) {
          step.data = action.payload.data;
        }
      });
      state.nsoFlow.loading = false;
      state.nsoFlow.error = null;
    });
    builder.addCase(nsoFlowFetchNsoRequestData.rejected, (state, action) => {
      state.nsoFlow.loading = false;
      state.nsoFlow.error = action.error;
    });
  },
});

export const { nsoFlowFetchData, nsoFlowFetchDataSuccess, nsoFlowFetchDataFailure } = nsoFlowSlice.actions;

export const selectNsoFlow = (state: RootState) => state.nsoFlow;

export const nsoFlowReducer = nsoFlowSlice.reducer;
nsoFlowSlice.reducer = (state: any, action: any) => {
  const newState = nsoFlowReducer(state, action);
  localStorage.setItem("nsoFlowState", JSON.stringify(newState));
  return newState;
};

export default nsoFlowSlice.reducer;
